// src/prf/prfCompare.js
// Comparing coverage levels, and the historical evidence underneath them.
//
// This is the half a rate card cannot do. RMA's own illustration prints 85% and
// 90% side by side and stops; what it never answers is whether the extra premium
// bought anything, because answering that needs the grid's own history replayed
// at BOTH levels. Everything here is deterministic and re-derivable from the
// stored actuarials — there is no model call, no score and no verdict.
//
// ⚠ IT DESCRIBES, IT DOES NOT RECOMMEND. `explainTrade` returns measured
// differences in plain English ("costs $1,015 more, protects $3,352 more, paid
// in 5 of 17 years either way"). It must never return "better", "worth it",
// "attractive" or "should" — that is [[feedback_present_data_not_verdicts]], and
// the whole reason this tool is auditable rather than a black box that says
// choose July-August.
//
// ⚠ A HIGHER COVERAGE LEVEL IS NOT JUST A BIGGER NUMBER. It raises the trigger:
// RMA pays whenever the index falls below the coverage level, so 90% pays in
// years 85% does not. That is why the comparison MUST replay history per level
// rather than scaling one level's result — scaling would keep the payout years
// fixed and understate exactly what the extra premium buys.

const { quotePrf, simulatePrf, roundHalfUp } = require("./prfQuote.js");
const { paymentFactor } = require("./prfIndexMethodology.js");

const LAB = ["JANFEB", "FEBMAR", "MARAPR", "APRMAY", "MAYJUN", "JUNJUL",
             "JULAUG", "AUGSEP", "SEPOCT", "OCTNOV", "NOVDEC"];

// The four lenses the history matrix can be read under. Each is a different
// QUESTION, and two of them are not interchangeable with the other two:
//   index          — RMA's published number. The fact.
//   rainfall       — inches. RECONSTRUCTED as index x normal unless a settled
//                    actual is supplied; always carries `reconstructed`.
//   payment_factor — what RMA would pay per dollar of THAT interval's
//                    protection at this coverage level. Allocation-independent,
//                    so it is comparable across all eleven columns and answers
//                    "which intervals have actually paid on this grid".
//   indemnity_ac   — dollars per acre AT THE CURRENT ELECTION. Zero, not blank,
//                    in an interval carrying no percent of value: the grower
//                    really would have received nothing there. Its row total
//                    ties to the year's indemnity in `simulatePrf`.
const LENSES = ["index", "rainfall", "payment_factor", "indemnity_ac"];

function r2(x) { return Math.round(x * 100) / 100; }

/**
 * Replay one block's election at several coverage levels.
 *
 * @param {object} a
 * @param {number} a.countyBaseValue
 * @param {number} a.productivityFactor
 * @param {number} a.acres
 * @param {number} [a.share=1]
 * @param {object} a.allocation           { INTERVAL: fraction }
 * @param {object} a.ratesByLevel         { 0.85: { INTERVAL: rate }, ... }
 * @param {object} a.series               { INTERVAL: { year: index } }
 * @param {number[]} a.levels             coverage levels to compare
 * @param {object} [a.rules]              per-county percent-of-value limits
 * @returns {{rows: object[], years: number, skipped: number}}
 */
function compareLevels(a) {
  const { countyBaseValue, productivityFactor, acres, share = 1,
          allocation, ratesByLevel, series, levels, rules } = a;
  const rows = [];
  let years = 0, skipped = 0;

  for (const cov of levels) {
    const rates = ratesByLevel?.[cov] || ratesByLevel?.[String(cov)] || null;
    const used = Object.keys(allocation || {}).filter((k) => Number(allocation[k]) > 0);
    const missing = used.filter((l) => !rates || rates[l] == null);
    // ⚠ A LEVEL WE CANNOT PRICE IS LISTED AND NAMED, NEVER DROPPED. A comparison
    // silently missing 90% reads as "90% is not offered here", which is a claim
    // about RMA's program rather than about our data.
    if (missing.length) {
      rows.push({ coverage_level: cov, priced: false, reason: "NO_RATE", intervals: missing });
      continue;
    }
    let quote;
    try {
      quote = quotePrf({
        countyBaseValue, coverageLevel: cov, productivityFactor,
        acres, share, allocation, rates, rules: rules || undefined,
      });
    } catch (e) {
      // Strip the engine's own prefix — a reader is being told why their
      // election cannot be written, not which function objected.
      rows.push({ coverage_level: cov, priced: false, reason: "REFUSED",
                  message: String(e.message || "").replace(/^prfQuote:\s*/, "") });
      continue;
    }
    const sim = series
      ? simulatePrf({ countyBaseValue, coverageLevel: cov, productivityFactor,
                      acres, share, allocation, rates, rules: rules || undefined, series })
      : { years: 0, skipped: 0, rows: [], summary: null };
    years = Math.max(years, sim.years);
    skipped = Math.max(skipped, sim.skipped);
    rows.push({
      coverage_level: cov, priced: true,
      protection: quote.total_protection,
      protection_per_acre: quote.protection_per_acre,
      total_premium: quote.total_premium,
      subsidy: quote.total_subsidy,
      subsidy_rate: quote.subsidy_rate,
      producer_premium: quote.producer_premium,
      producer_premium_per_acre: acres ? quote.producer_premium / acres : null,
      // The engine's OWN per-interval lines, carried through so a caller that
      // needs interval protection never rebuilds it. historyMatrix takes these
      // directly: re-deriving them from protection-per-acre is the drift
      // check-prf-compare caught once already.
      lines: quote.lines,
      history: sim.summary, history_rows: sim.rows,
    });
  }
  return { rows, years, skipped };
}

/**
 * The years x intervals matrix behind a quote.
 *
 * @param {object} a
 * @param {object} a.series      { INTERVAL: { year: index } }
 * @param {object} [a.normals]   { INTERVAL: normalInches } — enables the rainfall lens
 * @param {object} [a.actuals]   { INTERVAL: { year: inches } } settled rainfall, preferred
 * @param {object} a.allocation  { INTERVAL: fraction }
 * @param {number} a.coverageLevel
 * @param {object} a.quote       the quotePrf result for this election
 * @param {number} a.acres
 * @param {number[]} [a.years]   ascending; defaults to every year in the series
 */
function historyMatrix(a) {
  const { series, normals, actuals, allocation, coverageLevel, quote, acres } = a;
  // ⚠ THE INDEMNITY LENS READS THE QUOTE'S OWN LINES, NEVER RE-DERIVES THEM.
  // The first cut computed protectionPerAcre × percent × paymentFactor, which is
  // the same arithmetic by a different route and drifts: the engine rounds the
  // per-acre protection to cents and then the interval to a whole dollar
  // (569.93 × 100 × 0.5 → $28,497), while the direct product gives $28,496.25.
  // Under 1% that is pennies; in a year with a large payment factor it exceeded a
  // dollar and check-prf-compare's tie-out caught it. Two ways to compute one
  // number is precisely how this drawer would come to disagree with the quote
  // printed above it.
  const protByIv = {};
  for (const l of (quote?.lines || [])) protByIv[l.interval] = l.protection;
  // Protection per acre at 100% of value — the basis for `indemnity_ac_full`.
  // Taken from the quote rather than recomputed, and derived from an ELECTED
  // line (protection / (acres x percent)) so it carries the engine's own
  // rounding rather than a second derivation of CBV x coverage x productivity.
  const perAcre = (() => {
    if (a.protectionPerAcre != null) return Number(a.protectionPerAcre);
    const line = (quote?.lines || []).find((l) => Number(allocation?.[l.interval]) > 0 && l.protection > 0);
    if (!line || !acres) return null;
    const pct = Number(allocation[line.interval]);
    return pct > 0 ? line.protection / (acres * pct) : null;
  })();
  const yearSet = new Set();
  for (const l of LAB) for (const y of Object.keys(series?.[l] || {})) yearSet.add(Number(y));
  const years = (a.years || [...yearSet]).sort((x, y) => y - x); // newest first, as the AIP prints it

  const elected = {};
  for (const l of LAB) elected[l] = Number(allocation?.[l] || 0);

  const rows = years.map((year) => {
    const cells = {};
    for (const l of LAB) {
      const idx = series?.[l]?.[year];
      if (!Number.isFinite(idx)) { cells[l] = null; continue; }
      const pf = paymentFactor(coverageLevel, idx);
      const actual = actuals?.[l]?.[year];
      const normal = normals?.[l];
      const haveActual = Number.isFinite(actual);
      cells[l] = {
        index: idx,
        // Reconstruction is LABELLED, never quietly presented as a measurement.
        rainfall: haveActual ? actual : (Number.isFinite(normal) ? r2(idx * normal) : null),
        rainfall_reconstructed: !haveActual && Number.isFinite(normal),
        payment_factor: pf,
        // Whole-dollar indemnity for the interval, straight off the quote's own
        // protection, divided down to the acre for display only.
        indemnity: elected[l] > 0 ? Math.round((protByIv[l] || 0) * pf) : 0,
        indemnity_ac: elected[l] > 0 && acres
          ? r2(Math.round((protByIv[l] || 0) * pf) / acres) : 0,
        // ⚠ WHAT AN UNELECTED INTERVAL WOULD HAVE PAID, ON A STATED BASIS.
        // "As elected" is the money, and it is correctly blank where nothing was
        // elected — but that makes the lens useless for the comparison the
        // matrix exists to support ("which intervals actually pay here"). An
        // unelected interval has no percent of value, so there is no honest
        // as-elected figure for it; the fix is a DECLARED basis rather than an
        // invented percent. 100% of value is that basis: protection per acre is
        // CBV x coverage x productivity, so this is simply perAcre x payment
        // factor, and an elected cell is exactly this times its own percent.
        // Same normalization the interval replay loss ratio uses, for the same
        // reason — it removes the allocation so intervals are comparable.
        indemnity_ac_full: perAcre != null ? r2(perAcre * pf) : null,
        elected: elected[l] > 0,
      };
    }
    const priced = LAB.filter((l) => elected[l] > 0);
    const complete = priced.every((l) => cells[l] != null);
    return {
      year, cells, complete,
      // Ties to simulatePrf's per-year indemnity EXACTLY; the checker asserts it
      // at zero tolerance, which is only possible because both sides now sum the
      // same rounded per-interval dollars.
      indemnity: complete ? priced.reduce((s, l) => s + cells[l].indemnity, 0) : null,
      indemnity_ac: complete && acres
        ? r2(priced.reduce((s, l) => s + cells[l].indemnity, 0) / acres) : null,
    };
  });

  return { years, intervals: LAB, rows, lenses: LENSES };
}

/**
 * The trade between two priced levels, in sentences a person can check.
 * Returns [] when either level could not be priced — no rate, no claim.
 */
function explainTrade(lo, hi) {
  if (!lo?.priced || !hi?.priced) return [];
  const pct = (c) => Math.round(c * 100) + "%";
  const money = (n) => "$" + Math.round(n).toLocaleString();
  const out = [];

  const dPrem = hi.producer_premium - lo.producer_premium;
  const dProt = hi.protection - lo.protection;
  out.push(`Going from ${pct(lo.coverage_level)} to ${pct(hi.coverage_level)} costs ` +
           `${money(dPrem)} more in producer premium and protects ${money(dProt)} more.`);

  // The subsidy rate FALLS as coverage rises (55% at 85%, 51% at 90%), so the
  // producer's share climbs faster than the protection does. Stating it is not a
  // judgement — it is the mechanism behind the number above.
  if (hi.subsidy_rate < lo.subsidy_rate) {
    out.push(`RMA subsidises ${pct(hi.coverage_level)} at ${Math.round(hi.subsidy_rate * 100)}% ` +
             `against ${Math.round(lo.subsidy_rate * 100)}% at ${pct(lo.coverage_level)}, ` +
             `so the producer's share of the premium rises with the level.`);
    }

  const a = lo.history, b = hi.history;
  if (a && b && a.years) {
    out.push(`Over ${a.years} years on this grid, this election paid in ` +
             `${a.years_paid} of them at ${pct(lo.coverage_level)} and ${b.years_paid} at ${pct(hi.coverage_level)}` +
             (b.years_paid > a.years_paid
               ? ` — the higher level triggers in ${b.years_paid - a.years_paid} year(s) the lower one does not.`
               : `.`));
    out.push(`Average result per year, indemnity minus producer premium: ` +
             `${money(a.avg_net_per_year)} at ${pct(lo.coverage_level)}, ` +
             `${money(b.avg_net_per_year)} at ${pct(hi.coverage_level)}.`);
    if (Number.isFinite(a.longest_dry_spell)) {
      out.push(`Longest run of consecutive years with no payment: ` +
               `${a.longest_dry_spell} at ${pct(lo.coverage_level)}, ${b.longest_dry_spell} at ${pct(hi.coverage_level)}.`);
    }
    out.push(`History is what this grid did, not what it will do — the index is rainfall, ` +
             `and past intervals carry no claim on future ones.`);
  }
  return out;
}

/**
 * The whole quote at each coverage level — every block summed.
 *
 * ⚠ THE HISTORY IS SUMMED PER YEAR, NOT AVERAGED ACROSS BLOCKS. Two blocks on
 * different grids can pay in different years, and what a grower experiences is
 * the total in each year: a policy "paid" in a year if ANY block paid. Averaging
 * two blocks' payout frequencies would report a number no year ever produced.
 *
 * ⚠ AND A YEAR IS ONLY COUNTED WHERE EVERY BLOCK CAN ANSWER FOR IT. A block whose
 * grid history is shorter would otherwise contribute zero indemnity against a
 * full premium, which understates the record — the same rule simulatePrf follows
 * within one block, applied across them.
 *
 * @param {object[]} blocks  each { countyBaseValue, productivityFactor, acres,
 *                           share, allocation, ratesByLevel, series, rules, label }
 * @param {number[]} levels
 */
function comparePolicy(blocks, levels) {
  const out = [];
  for (const cov of levels) {
    const per = [];
    let ok = true;
    const unpriceable = [];
    for (const b of blocks) {
      const one = compareLevels({ ...b, levels: [cov] }).rows[0];
      // Carry the REASON and its message through, not just the fact of failure —
      // "no published rate" and "this election is not legal" send a reader to
      // two different places, and only one of them is fixable on this screen.
      if (!one.priced) {
        ok = false;
        unpriceable.push({ label: b.label, reason: one.reason,
                           intervals: one.intervals, message: one.message });
      }
      per.push(one);
    }
    const priced = per.filter((p) => p.priced);
    if (!priced.length) { out.push({ coverage_level: cov, priced: false, reason: "NO_RATE", blocks_unpriceable: unpriceable }); continue; }

    // Per-year totals across the blocks that carry a history.
    const yearSets = priced.map((p) => new Set((p.history_rows || []).map((r) => r.year)));
    const common = yearSets.length
      ? [...yearSets[0]].filter((y) => yearSets.every((s) => s.has(y))).sort((a, b) => a - b)
      : [];
    let hist = null;
    if (common.length) {
      const rows = common.map((y) => {
        let ind = 0, prem = 0;
        for (const p of priced) {
          const r = (p.history_rows || []).find((x) => x.year === y);
          ind += r ? r.indemnity : 0; prem += r ? r.producer_premium : 0;
        }
        return { year: y, indemnity: ind, producer_premium: prem, net: ind - prem };
      });
      const paid = rows.filter((r) => r.indemnity > 0);
      const totI = rows.reduce((s, r) => s + r.indemnity, 0);
      const totP = rows.reduce((s, r) => s + r.producer_premium, 0);
      let run = 0, dry = 0;
      for (const r of rows) { run = r.indemnity > 0 ? 0 : run + 1; dry = Math.max(dry, run); }
      hist = {
        years: rows.length, years_paid: paid.length, payout_freq: paid.length / rows.length,
        total_indemnity: totI, total_producer_premium: totP, net: totI - totP,
        ratio: totP ? totI / totP : null, avg_net_per_year: (totI - totP) / rows.length,
        longest_dry_spell: dry,
        best: rows.reduce((x, y) => (y.net > x.net ? y : x)),
        worst: rows.reduce((x, y) => (y.net < x.net ? y : x)),
      };
    }
    const sum = (k) => priced.reduce((s, p) => s + (p[k] || 0), 0);
    const acres = blocks.reduce((s, b) => s + (Number(b.acres) || 0), 0);
    // ⚠ PROTECTION PER ACRE IS PUBLISHED, NOT DIVIDED. It is CBV × level ×
    // productivity — the figure the AIP prints and a grower re-computes on a
    // notepad. Deriving it as total ÷ acres instead gives $280.49 where RMA says
    // $280.50, because the total is a sum of per-interval figures each rounded to
    // a whole dollar. A cent wrong on a number the reader can check by hand costs
    // more trust than it saves effort. Null where blocks disagree — a blended
    // per-acre across two different county base values is a third number nobody
    // published.
    const perAcres = [...new Set(priced.map((p) => roundHalfUp(p.protection_per_acre, 2)))];
    const protection_per_acre = perAcres.length === 1 ? perAcres[0] : null;
    out.push({
      coverage_level: cov, priced: true, complete: ok,
      blocks_priced: priced.length, blocks_total: blocks.length, blocks_unpriceable: unpriceable,
      protection: sum("protection"), total_premium: sum("total_premium"),
      protection_per_acre,
      subsidy: sum("subsidy"), subsidy_rate: priced[0].subsidy_rate,
      producer_premium: sum("producer_premium"),
      producer_premium_per_acre: acres ? sum("producer_premium") / acres : null,
      history: hist, history_rows: hist ? common.length : 0,
    });
  }
  return out;
}

module.exports = { compareLevels, comparePolicy, historyMatrix, explainTrade, LENSES, LAB };
